import React from 'react';
import { Box } from '@mui/material';
import { Panel } from '../layout/Panel';
import { SegmentedControl } from '../inputs/SegmentedControl';
import { useElementWidth } from '../hooks/useElementWidth';

/** px. Narrower than this, the frame asks its buttons to drop their words. */
const COMPACT_BELOW = 520;

export interface ResultView {
  value: string;
  label: string;
}

export interface ResultFrameProps {
  /** Leave out where something around the frame already names the result. */
  title?: string;
  /** A few short facts, joined with dots: "7 rows", "Snowflake", "3 hours ago". */
  meta?: string[];
  /** Ways to look at the result — Table, Chart, SQL. Shown when there are two or more. */
  views?: ResultView[];
  view?: string;
  onViewChange?: (view: string) => void;
  /**
   * Buttons on the right of the header: Download, Pin to dashboard. Pass a
   * function to be told when the frame is narrow (`compact`), and drop the
   * buttons' words then so they stay on one line.
   */
  actions?: React.ReactNode | ((compact: boolean) => React.ReactNode);
  /**
   * `pane` — fills a results pane: the panel on the page's canvas, scrolling.
   * `card` — the panel alone, sized to its content, for inside a conversation.
   */
  variant?: 'pane' | 'card';
  /** The result runs edge to edge — a table — rather than inside padding. */
  flush?: boolean;
  /** Offer an Expand button that shows this — the chart, drawn to fill — nearly full screen. */
  expanded?: { content: React.ReactNode; title?: React.ReactNode };
  children: React.ReactNode;
}

/**
 * One result — a table, a chart, a number — in the same `Panel` every board
 * chart and table sits in, so a result looks the same wherever it appears.
 *
 * The actions always sit on the right of the header. What sits on the left
 * depends on what the result has, so nothing gets squeezed:
 * - a title (a card in a conversation) — the title, with the facts under it;
 * - views and no title (the results pane) — the view switch, with the facts
 *   in the panel's footer strip, where there is room for all of them;
 * - neither — the facts.
 */
export function ResultFrame({
  title,
  meta = [],
  views = [],
  view,
  onViewChange,
  actions,
  variant = 'pane',
  flush = false,
  expanded,
  children,
}: ResultFrameProps) {
  const pane = variant === 'pane';
  const [measure, width] = useElementWidth();
  // Below this width a view switch and two labelled buttons don't share a line.
  const compact = width > 0 && width < COMPACT_BELOW;
  const shownActions = typeof actions === 'function' ? actions(compact) : actions;
  const facts = meta.filter(Boolean).join(' · ') || undefined;
  const hasViews = views.length > 1 && view !== undefined && Boolean(onViewChange);
  const switcher = hasViews ? (
    <SegmentedControl value={view as string} options={views} onChange={onViewChange as (v: string) => void} ariaLabel="View" />
  ) : null;
  // With a title the header holds the name and facts, so the switch goes in
  // the body; without one, the switch takes the header and the facts drop to
  // the footer.
  const switchInHeader = hasViews && !title;

  const panel = (
    <Panel
      headerStart={switchInHeader ? switcher : undefined}
      title={title}
      subtitle={switchInHeader ? undefined : facts}
      footer={switchInHeader ? facts : undefined}
      actions={shownActions}
      // One row: a result's facts and its one or two buttons share a line,
      // rather than each taking a band of their own above a single number.
      compact
      flush
      bounded={pane}
      expanded={expanded}
    >
      {hasViews && !switchInHeader && <Box sx={{ px: 2, pt: 1.5 }}>{switcher}</Box>}
      <Box
        sx={{
          p: flush ? 0 : 2,
          pt: hasViews && !switchInHeader ? 1.5 : flush ? 0 : 2,
          minWidth: 0,
          // In a pane the result gets the room left under the header, so a
          // long table scrolls inside it rather than past the page.
          ...(pane && { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
      >
        {children}
      </Box>
    </Panel>
  );

  if (variant === 'card') return <Box ref={measure}>{panel}</Box>;
  // The panel is never taller than the pane: it ends above the page's edge
  // and scrolls inside, however many rows the result has.
  return (
    <Box
      sx={(t) => ({
        height: '100%',
        overflow: 'hidden',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: t.tokens.surface.canvas,
      })}
    >
      <Box ref={measure} sx={{ flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {panel}
      </Box>
    </Box>
  );
}
